<script>
import { PhxIcon, PhxWizard, PhxWizardStep } from '@phx-styleguides/jlc';
import PictureLazy from '@maison/components/common/PictureLazy';
import { difference, filter, find, intersection, map, pickBy, set } from 'lodash-es';
import { clientApi } from '@phoenix/clientApi';
import BackgroundVideo from '@maison/components/molecules/BackgroundVideo.vue';
import MixedGrid from '@maison/components/mixedGrid/MixedGrid.vue';

export default {
  components: {
    BackgroundVideo,
    MixedGrid,
    PictureLazy,
    PhxWizard,
    PhxWizardStep,
    PhxIcon,
  },

  props: {
    title: {
      type: String,
      required: true,
    },
    preTitle: {
      type: String,
      default: '',
    },
    text: {
      type: String,
      required: true,
    },
    ctaText: {
      type: String,
      required: true,
    },

    products: {
      type: Array,
      required: true,
    },

    stepData: {
      type: Array,
      required: true,
    },

    allProductUrl: {
      type: String,
      default: '',
    },

    theme: {
      type: String,
      default: 'light',
    },

    image: {
      type: Object,
      default: null,
    },

    video: {
      type: Object,
      default: null,
    },

    introStyles: {
      type: String,
      default: '',
    },

    gradient: {
      type: String,
      default: '',
    },

    background: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      productPages: [],
      isLoading: false,
      hasError: false,
      current: 0,
      shouldDisplayResults: false,
      sliderId: 'gift-finder-product-slider',
      selectedChoices: {},
    };
  },

  computed: {
    currentStep() {
      return find(this.stepData, (step) => step.number === this.current);
    },

    isLastStep() {
      return this.current === this.stepData.length;
    },

    introStepClasses() {
      return ['gift-finder-step gift-finder-intro', { 'theme-dark': this.theme === 'dark' }];
    },

    stepClasses() {
      return ['gift-finder-step gift-finder-qa', { 'theme-dark': this.theme === 'dark' }];
    },

    bgStyle() {
      if (this.hasGradient) {
        return `--background: ${this.gradient};`;
      }

      if (this.hasBackground) {
        return `--background: url("${this.background}");`;
      }

      return '';
    },

    addTracking() {
      return JSON.stringify(window.tracking.getGiftingGuideTracking());
    },

    chosenProducts() {
      let products = this.products;

      if (Object.keys(this.selectedChoices).length === 0) {
        return products;
      }

      Object.values(this.selectedChoices).forEach((choice) => {
        products = filter(products, (product) =>
          choice.operator === 'or'
            ? intersection(choice.value, product.propertyValues).length > 0
            : difference(choice.value, product.propertyValues).length === 0,
        );
      });

      return products;
    },

    hasBackground() {
      return this.background !== '';
    },

    hasGradient() {
      return this.gradient !== '';
    },

    filteredSteps() {
      return this.stepData.map((step) => ({
        ...step,
        choices: step.choices.filter((choice) => !choice.disabled),
      }));
    },
  },

  methods: {
    updateValues(choice) {
      set(this.selectedChoices, this.current, choice);
    },

    removeValues() {
      this.selectedChoices = pickBy(this.selectedChoices, (choice, key) => key < this.current);
    },

    disableChoices() {
      if (this.current === 0) {
        return;
      }

      this.currentStep.choices.forEach((choice) => {
        const products = filter(this.chosenProducts, (product) =>
          choice.operator === 'or'
            ? intersection(choice.value, product.propertyValues).length > 0
            : difference(choice.value, product.propertyValues).length === 0,
        );
        choice.disabled = products.length === 0;
      });
    },

    goToNextStep(choice) {
      this.$refs.wizard.goToNextStep();
      this.updateValues(choice);
      this.goForward();
      this.disableChoices();
    },

    goToPreviousStep() {
      this.$refs.wizard.goToPreviousStep();
      if (this.current > 0) {
        this.current -= 1;
        this.removeValues();
        this.disableChoices();
      }
    },

    goToFirstStep() {
      this.$refs.wizard.goToFirstStep();
      this.reset();
      this.shouldDisplayResults = false;
    },

    skipStep() {
      this.$refs.wizard.goToNextStep();
      this.goForward();
      this.disableChoices();
    },

    goForward() {
      if (this.isLastStep) {
        this.submit();
      } else {
        this.current += 1;
      }
    },

    async submit() {
      await this.getGiftFinderProductPages(map(this.chosenProducts, 'id'));
      this.reset();
      this.pushTracking();
    },

    pushTracking() {
      const items = map(this.productPages, (page) => ({
        item_id: page.modelReference,
        item_name: page.name,
        item_collection: page.collection,
      }));

      window.tracking.pushEvent(window.tracking.getGiftingGuideResultTracking(items.length, items));
    },

    reset() {
      this.current = 0;
      this.selectedChoices = {};
    },

    getStepTracking(step, cta) {
      return JSON.stringify(window.tracking.getGiftingGuideStepTracking(step, cta));
    },

    async getGiftFinderProductPages(pages) {
      this.isLoading = true;

      try {
        const requestResult = await clientApi.get(window.route('api.search.giftFinderProductPages'), {
          params: {
            pages,
          },
        });

        this.productPages = requestResult?.data?.data?.results;
        this.shouldDisplayResults = true;
      } catch (error) {
        window.Phoenix.sentry.captureException(error);
        window.Phoenix.toaster.notify({
          type: 'danger',
          text: error.response.data.message,
        });
      }

      this.isLoading = false;
    },

    getStepChoicesClasses(choices) {
      const nbChoices = choices.reduce((acc, choice) => (choice.disabled ? acc : acc + 1), 0);
      const simpleClasses = nbChoices >= 5 ? '' : `md:grid-cols-${nbChoices}`;
      return [
        'gift-finder-qa-answers',
        simpleClasses,
        {
          'xs:grid-cols-2 md:grid-cols-3 xl:grid-cols-5': nbChoices === 5,
          'xs:grid-cols-2 lg:grid-cols-3': nbChoices === 6,
        },
      ];
    },

    getChoiceImage(images, choices) {
      const nbChoices = choices.reduce((acc, choice) => (choice.disabled ? acc : acc + 1), 0);
      return images[nbChoices];
    },
  },
};
</script>

<template>
  <div data-cy="gift-finder">
    <phx-wizard v-cloak id="gift-finder" ref="wizard" class="gift-finder">
      <template #default="slotProps">
        <phx-wizard-step key="wizard-step-intro" :class="introStepClasses" scroll-into-view="end">
          <div
            class="hero-banner container hero-banner--1-col hero-banner--immersive hero-banner--full container--tight hero-banner--cover hero-banner--centered hero-banner--no-pb"
          >
            <div class="hero-banner__col">
              <div class="overlay overlay--gradient hero-banner__overlay" :style="introStyles"></div>
              <div class="hero-banner__media">
                <background-video v-if="video" v-bind="video" />
                <div v-else-if="image" class="picture picture--cover hero-banner__picture">
                  <picture-lazy v-bind="image" />
                </div>
              </div>

              <div class="hero-banner__wrapper container grid">
                <div class="hero-banner__container hero-banner__container--boxed">
                  <div v-if="preTitle" class="hero-banner__pretitle h6">{{ preTitle }}</div>
                  <h2 class="hero-banner__title">{{ title }}</h2>
                  <div class="hero-banner__text">{{ text }}</div>

                  <div class="hero-banner__actions">
                    <button
                      type="button"
                      class="btn btn--default btn--sm expand-target"
                      :data-tracking="addTracking"
                      data-cy="gift-finder-start-btn"
                      @click="skipStep()"
                    >
                      <span class="btn__text">{{ ctaText }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </phx-wizard-step>

        <phx-wizard-step
          v-for="(step, key) in filteredSteps"
          :key="`wizard-step-${key}`"
          :class="stepClasses"
          :style="bgStyle"
          :data-cy="'gift-finder-step-' + key"
          scroll-into-view="end"
        >
          <div class="container gift-finder-qa-body">
            <header class="gift-finder-qa-header">
              <div
                class="progress"
                :style="`--value: ${slotProps.steps.current * (100 / (slotProps.steps.total - 2))}%`"
              ></div>

              <div class="gift-finder-qa-header__steps" data-cy="gift-finder-current-step">
                <button
                  type="button"
                  class="btn btn--link btn--icon btn--reverse gift-finder-qa-header__back btn--grey"
                  data-cy="gift-finder-previous-btn"
                  @click="goToPreviousStep()"
                >
                  <span class="btn__text">{{ $t('GiftFinder.step.back') }}</span>
                  <span class="btn__icon">
                    <phx-icon icon="chevron-left-10" class="icon--arrow" bidirectional />
                  </span>
                </button>

                {{ $t('GiftFinder.step', { current: slotProps.steps.current, total: slotProps.steps.total - 2 }) }}

                <button
                  type="button"
                  class="btn btn--link btn--icon btn--grey"
                  :data-tracking="getStepTracking(slotProps.steps.current, $t('GiftFinder.step.skip'))"
                  data-cy="gift-finder-skip-btn"
                  @click="skipStep()"
                >
                  <span class="btn__text">{{ $t('GiftFinder.step.skip') }}</span>
                  <span class="btn__icon">
                    <phx-icon icon="chevron-10" class="icon--10 icon--arrow" bidirectional />
                  </span>
                </button>
              </div>
            </header>

            <div class="h4 gift-finder-qa-title">{{ step.title }}</div>

            <div :class="getStepChoicesClasses(step.choices)">
              <template v-for="(choice, index) in step.choices" :key="index">
                <div v-if="!choice.disabled" class="media-card media-card--actionable">
                  <div class="overlay overlay--gradient media-card__overlay"></div>
                  <button
                    class="media-card__content"
                    :disabled="choice.disabled"
                    :data-tracking="getStepTracking(slotProps.steps.current, choice.label)"
                    data-cy="gift-finder-choice-btn"
                    @click="goToNextStep(choice)"
                  >
                    <div class="h5 gift-finder-qa-answers__title">{{ choice.label }}</div>
                    <p v-if="choice.subText" class="text-sm">{{ choice.subText }}</p>
                  </button>

                  <div
                    v-if="choice.images && getChoiceImage(choice.images, step.choices)"
                    class="picture picture--cover"
                  >
                    <picture-lazy v-bind="getChoiceImage(choice.images, step.choices)" />
                  </div>
                </div>
              </template>
            </div>
          </div>
        </phx-wizard-step>

        <phx-wizard-step
          key="wizard-step-outro"
          class="gift-finder-step"
          data-cy="gift-finder-step-outroduction"
          scroll-into-view="end"
        >
          <div class="gift-finder-outroduction">
            <section class="section text-block section--text text-center">
              <div class="container grid">
                <div class="text-block__wrapper">
                  <h6 v-if="preTitle">{{ preTitle }}</h6>
                  <h2 class="text-block__title">{{ $t('GiftFinder.perfectGiftForYou') }}</h2>
                </div>
              </div>
            </section>

            <section class="section">
              <div v-if="isLoading" class="flex flex-grid--center items-center" style="height: 50vh">
                <div class="loader loader--dark">
                  <div class="loader__inner"></div>
                </div>
              </div>

              <div v-else class="container">
                <mixed-grid
                  v-if="shouldDisplayResults"
                  :all-sorted-items="productPages"
                  :unique-id="sliderId"
                  :has-load-more="false"
                />
                <div class="duo-button gift-finder-outroduction__buttons-wrapper">
                  <div class="duo-button__wrapper">
                    <a :href="allProductUrl" class="btn btn--default btn--full btn--negative">
                      <span class="btn__text">{{ $t('GiftFinder.step.viewAll') }}</span>
                    </a>
                    <button type="button" class="btn btn--default btn--full" @click="goToFirstStep">
                      <span class="btn__text">{{ $t('GiftFinder.takeQuizzAgain') }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </phx-wizard-step>
      </template>
    </phx-wizard>
  </div>
</template>
